import {isEqualWith, uniqBy} from 'lodash';
import {type ActionType as TSActionType, getType} from 'typesafe-actions';

import {fromServerResult, getLastUpdatedWithWindow} from '../../util/runDeltas';
import * as Run from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import {parseGqlDeltaOp} from '../runs-low/api';
import {type LeanRunSelectorGroup, type RunSelectorRunsData} from './actions';
import * as Actions from './actions';
import {deleteRun, updateRun, updateRuns, updateRunTags} from './utils';

export const EMPTY_DATA: RunSelectorRunsData = {
  lastUpdatedAt: new Date(0).toISOString(),
  runs: [],
  totalRuns: 0,
  pinnedRuns: [],
};

export type StateType = {
  /**
   * There may be multiple active RunSelectorRun queries on a single page.
   * For example, a report may contain multiple panel grids, which each
   * have a dedicated run selector with its own RSR results. Another example
   * is if grouping is applied, each expanded group is a sub-query.
   * Each key in this state represents the results of a distinct RSR query.
   */
  [queryId: string]: RunSelectorRunsData;
};

export type ActionType = TSActionType<typeof Actions>;

export type LeanRunSelectorGroupRunMetadata = {
  __runSelectorGroupValues?: Record<string, RunTypes.Value | undefined>;
};

function valueFromLeanGroup(
  groupKey: string | undefined,
  group: LeanRunSelectorGroup
) {
  // Group IDs are "<groupKey>:<JSON-encoded value>", with the bare, unquoted
  // "null" suffix reserved for the null group (a group actually named "null"
  // is JSON-encoded as `"null"`). The SQL fast path also sends `value: null`
  // for the null group, but the in-memory fallback resolver sends the string
  // "null", so the ID is the reliable signal across both backend paths.
  if (groupKey != null && group.id === `${groupKey}:null`) {
    return null;
  }
  if (!groupKey?.startsWith('config.')) {
    return (group.value ?? null) as RunTypes.Value;
  }

  const idPrefix = `${groupKey}:`;
  if (group.id.startsWith(idPrefix)) {
    const rawValue = group.id.slice(idPrefix.length);
    try {
      return JSON.parse(rawValue) as RunTypes.Value;
    } catch (err) {
      console.error(
        `Failed to parse lean run selector group value from id "${group.id}"`,
        err
      );
    }
  }

  return (group.value ?? null) as RunTypes.Value;
}

function runFromLeanGroup(
  groupKey: string | undefined,
  group: LeanRunSelectorGroup
): RunTypes.Run {
  const label = group.label;
  const value = valueFromLeanGroup(groupKey, group);

  // Only the field the rows are grouped by deviates from the defaults below.
  // Run fields are readonly, so collect the deviation as an override instead
  // of mutating the run.
  let overrides: Partial<RunTypes.Run> = {};
  if (groupKey?.startsWith('config.')) {
    overrides = {
      config: {[groupKey.slice('config.'.length)]: value} as RunTypes.KeyVal,
    };
  } else {
    switch (groupKey) {
      case 'group':
        // Keep the null group's name as a real null: the group-row link maps
        // null to the special `<null>` URL token, whereas the lean query's
        // label for the null bucket is the string "null", which would produce
        // a /groups/null link to a nonexistent group.
        overrides = {group: value == null ? null : label};
        break;
      case 'host':
        overrides = {host: label};
        break;
      case 'jobType':
        overrides = {jobType: label};
        break;
      case 'state':
        overrides = {state: label};
        break;
      case 'sweep':
      case 'sweepName':
        overrides = {sweep: {name: label, displayName: label}};
        break;
      case 'username':
        overrides = {user: {username: label}};
        break;
      // 'displayName' and 'name' need no case: both default to the label.
    }
  }

  const run: RunTypes.Run & LeanRunSelectorGroupRunMetadata = {
    _wandb: {},
    aggregations: {max: {}, min: {}},
    __runSelectorGroupValues: groupKey ? {[groupKey]: value} : {},
    config: {} as RunTypes.KeyVal,
    createdAt: new Date(0).toISOString(),
    displayName: label,
    group: null,
    // Matches the shape of the legacy grouped query: the second-to-last entry
    // is the subgroup count (when deeper grouping levels exist) and the last
    // entry is the run count.
    groupCounts:
      group.subgroupCount != null
        ? [group.subgroupCount, group.count]
        : [group.count],
    heartbeatAt: new Date(0).toISOString(),
    host: null,
    id: group.id,
    jobType: null,
    // The group row's active-run indicator reads the distinct states in
    // multiValueMetadataColumns; the lean query only reports whether any run
    // in the group is running, so surface just that signal.
    multiValueMetadataColumns: {state: group.hasActiveRuns ? ['running'] : []},
    name: label,
    notes: '',
    state: '',
    stopped: null,
    summary: {},
    sweep: null,
    tags: [],
    updatedAt: new Date(0).toISOString(),
    user: {},
    ...overrides,
  };
  return run;
}

function isExactValueEqual(left: unknown, right: unknown) {
  // Preserve Object.is number semantics: NaN is stable, but +0 and -0 differ.
  if (typeof left === 'number' && typeof right === 'number') {
    return Object.is(left, right);
  }
  // fromServerResult rebuilds the runs array but reuses unchanged run objects.
  if (
    Array.isArray(left) &&
    Array.isArray(right) &&
    left.length === right.length &&
    left.every((value, index) => Object.is(value, right[index]))
  ) {
    return true;
  }
  return undefined;
}

export function runSelectorRuns(
  state: StateType = {},
  action: ActionType
): StateType {
  switch (action.type) {
    case getType(Actions.processQueryResults): {
      const {
        queryId,
        data: {runs, pinnedRuns},
      } = action.payload;
      const existing = state[queryId] ?? EMPTY_DATA;

      const incoming = {
        delta: runs.delta.map(d => parseGqlDeltaOp(d)),
        totalRuns: runs.totalCount,
        deltaOrder: runs.deltaOrder,
      };
      const combined = fromServerResult(incoming, existing);
      combined.runs = uniqBy(combined.runs, 'id');
      combined.lastUpdatedAt = getLastUpdatedWithWindow(combined.lastUpdatedAt);
      const nextData: RunSelectorRunsData = {
        ...combined,
        pinnedRuns: pinnedRuns?.map(run => Run.fromJson(run)!) ?? [],
      };

      if (
        state[queryId] != null &&
        isEqualWith(existing, nextData, isExactValueEqual)
      ) {
        return state;
      }

      return {
        ...state,
        [queryId]: nextData,
      };
    }
    case getType(Actions.processLeanGroupResults): {
      const {
        queryId,
        groupKeys,
        data: {groups, totalCount},
      } = action.payload;
      return {
        ...state,
        [queryId]: {
          lastUpdatedAt: getLastUpdatedWithWindow(new Date().toISOString()),
          runs: groups.map(group => runFromLeanGroup(groupKeys[0], group)),
          totalRuns: totalCount,
          pinnedRuns: [],
        },
      };
    }
    case getType(Actions.loadRunSelectorRunsFromCache): {
      const {queryId, data} = action.payload;
      return {
        ...state,
        [queryId]: data,
      };
    }
    case getType(Actions.removeQuery): {
      const {queryId} = action.payload;
      if (!state[queryId]) {
        return state;
      }
      const nextState = Object.assign({}, state);
      delete nextState[queryId];
      return nextState;
    }
    case getType(Actions.deleteRun): {
      const {runName} = action.payload;
      return updateRuns(state, runs => deleteRun(runs, runName));
    }
    case getType(Actions.updateRun): {
      const {id: runName, vars} = action.payload;
      return updateRuns(state, runs => updateRun(runs, runName, vars));
    }
    case getType(Actions.updateRunTags): {
      const {tagMap} = action.payload;
      return updateRuns(state, runs => updateRunTags(runs, tagMap));
    }
    default: {
      return state;
    }
  }
}
